import { __classPrivateFieldGet, __esDecorate, __runInitializers, __setFunctionName } from "tslib";
import '#library/temporal.polyfill.js';
import { Immutable, Serializable } from '#library/class.library.js';
import { asArray, asError } from '#library/coercion.library.js';
import { getStorage, setStorage } from '#library/storage.library.js';
import { secure, proxify, delegate, indexedArray } from '#library/proxy.library.js';
import { getContext, CONTEXT } from '#library/utility.library.js';
import { enumify } from '#library/enumerate.library.js';
import { ownKeys, ownEntries, unwrap } from '#library/primitive.library.js';
import { getAccessors, omit } from '#library/reflection.library.js';
import { pad, trimAll } from '#library/string.library.js';
import { getType } from '#library/type.library.js';
import { clone } from '#library/serialize.library.js';
import { isEmpty, isDefined, isUndefined, isString, isObject, isSymbol, isFunction, isClass, isZonedDateTime, isDurationLike, isNumber } from '#library/assertion.library.js';
import { instant, getTemporalIds } from '#library/temporal.library.js';
import { getDateTimeFormat, getHemisphere, canonicalLocale, getISOWeekOfYear } from '#library/international.library.js';
import { LOG } from '#library/logger.class.js';
import { registerPlugin, interpret, ensureModule } from './plugin/plugin.util.js';
import { registerTerm, getTermRange } from './plugin/term/term.util.js';
import { AliasEngine } from './engine/engine.alias.js';
import { PatternCompiler } from './engine/engine.pattern.js';
import { createMasterGuard } from './engine/engine.guard.js';
import { DEFAULT_LAYOUT_CLASS, resolveLayoutOrder, getLayoutOrder } from './engine/engine.layout.js';
import { validateLicenseState, getLicenseSnapshot, setLicense, getLicenseState, updateScopeStatus } from './plugin/license/license.manager.js';
import { resolveMonthDay, setProperty, proto, hasOwn, resolveDisplayStatus } from './support/support.util.js';
import { datePattern } from './support/support.default.js';
import { sym, markConfig, TermError, getRuntime, init, extendState, setPatterns, isTempo, registryUpdate, registryReset, onRegistryReset, Token, Default, Guard, enums, STATE, LICENSE, DISCOVERY, $Internal, $setConfig, $Identity, $setEvents, $setPeriods, $setAliases, $buildGuard, $IsBase, $Tempo, $Register, $errored, $guard, $Discover, $setDiscovery, $LogConfig, $ImmutableSkip, $updateScopeStatus, logError, logDebug, logWarn, logTempo, setLogLevel, createCacheFacade } from '#tempo/support';
import { TEMPO_VERSION } from './tempo.version.js';
import { Interval } from './interval.class.js';
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
/** current execution context */ const Context = getContext();
/**  */ const ClassStates = new WeakMap();
// shortcut functions to common Tempo properties / methods
/** current timestamp (ts) */ export const getStamp = ((tempo, options) => new Tempo(tempo, options).ts);
/** create new Tempo */ export const getTempo = ((tempo, options) => new Tempo(tempo, options));
/** format a Tempo */ export const fmtTempo = ((fmt, tempo, options) => new Tempo(tempo, options).format(fmt));
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
/**
 * # Tempo
 * A powerful wrapper around `Temporal.ZonedDateTime` for flexible parsing and intuitive manipulation of date-time objects.
 * Bridges the gap between raw string/number inputs and the strict requirements of the ECMAScript Temporal API.
 */
/** Tempo state for the global configuration */
let _global = {};
/** cache for next-available 'usr' Token key */
let _usrCount = 0;
/** flag to prevent recursion during init */
const _lifecycle = { bootstrap: true, initialising: false, extendDepth: 0, ready: false };
const intervalProxyHandler = {
    construct(target, args) {
        const parse = (arg) => arg === null ? null : (arg instanceof Tempo ? arg : new Tempo(arg));
        return new target(parse(args[0]), parse(args[1]));
    }
};
let Tempo = (() => {
    var _Tempo_versions, _Tempo_terms_get, _Tempo_formatLicense, _Tempo_termMap, _Tempo_setSphere, _Tempo_isMonthDay, _Tempo_swapLayout, _Tempo_locale, _Tempo_getConfig, _Tempo_getEpoch;
    let _classDecorators = [Serializable, Immutable];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var Tempo = _classThis = class {
        static { _classThis = this; }
        static { __setFunctionName(this, "Tempo"); }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Tempo = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        /** Interval class for checking overlaps and bounds between Temporal points */ static Interval = new Proxy(Interval, intervalProxyHandler);
        /** Weekday names (short-form) */ static get WEEKDAY() { return enums.WEEKDAY; }
        /** Weekday names (long-form) */ static get WEEKDAYS() { return enums.WEEKDAYS; }
        /** Month names (short-form) */ static get MONTH() { return enums.MONTH; }
        /** Month names (long-form) */ static get MONTHS() { return enums.MONTHS; }
        /** Time durations as seconds (singular) */ static get DURATION() { return enums.DURATION; }
        /** Time durations as milliseconds (plural) */ static get DURATIONS() { return enums.DURATIONS; }
        /** Quarterly Seasons */ static get SEASON() { return enums.SEASON; }
        /** Compass cardinal points */ static get COMPASS() { return enums.COMPASS; }
        /** Tempo to Temporal DateTime Units map */ static get ELEMENT() { return enums.ELEMENT; }
        /** Pre-configured format {name -> string} pairs */ static get FORMAT() { return enums.FORMAT; }
        /** Number names (0-10) */ static get NUMBER() { return enums.NUMBER; }
        /** TimeZone aliases */ static get TIMEZONE() { return enums.TIMEZONE; }
        /** regional date-parsing configuration */ static get MONTH_DAY() { return enums.MONTH_DAY; }
        /** initialization strategies */ static get MODE() { return enums.MODE; }
        /** cache operation modes */ static get CACHE() { return enums.CACHE; }
        /** some useful Dates */ static get LIMIT() { return enums.LIMIT; }
        /** @internal check if Tempo is currently initializing */ static get isInitializing() { return !_lifecycle.ready; }
        /** @internal check if Tempo is currently extending */ static get isExtending() { return _lifecycle.extendDepth > 0; }
        static {
            _Tempo_versions = { value: { Tempo: TEMPO_VERSION } };
        }
        /** the active versions of Tempo and any registered plugins or modules */
        static get versions() { return Object.freeze({ ...__classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_versions) }); }
        /** the version of this Tempo build (stamped at build-time from package.json) */
        static get version() { return __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_versions)['Tempo']; }
        /** high-performance in-memory cache facade for glossary and dynamic parse results */
        static get cache() {
            return createCacheFacade(() => this[$Internal]());
        }
        /** human-readable formatted license state */
        static get license() {
            return __classPrivateFieldGet(Tempo, _classThis, "m", _Tempo_formatLicense).call(Tempo, this[$Internal]());
        }
        /** @internal programmatically update status of a license scope (e.g. when network response determines revocation) */
        static [(_Tempo_terms_get = function _Tempo_terms_get() { return Tempo[$Internal]().pluginsDb.terms; }, _Tempo_formatLicense = function _Tempo_formatLicense(state) {
            const { jws, key, ...raw } = getLicenseSnapshot(state);
            const ss = { timeStamp: 'ss' };
            const scopesSource = (raw.scopes && isObject(raw.scopes)) ? raw.scopes : {};
            const scopes = Object.fromEntries(Object.entries(scopesSource).map(([key, scope]) => {
                const s = scope;
                return [key, {
                        ...s,
                        ...(isNumber(s.exp) && { exp: new Tempo(s.exp, ss).fmt.weekTime }),
                        ...(isNumber(s.updated_at) && { updated_at: new Tempo(s.updated_at, ss).fmt.weekTime }),
                    }];
            }));
            return secure({
                ...raw,
                status: resolveDisplayStatus(raw.status),
                scopes,
                ...(isNumber(raw.expires) && { expires: new Tempo(raw.expires, ss).fmt.weekTime }),
                ...(isNumber(raw.issuedAt) && { issuedAt: new Tempo(raw.issuedAt, ss).fmt.weekTime }),
            });
        }, $updateScopeStatus)](scopeKey, status, error) {
            updateScopeStatus(this[$Internal](), scopeKey, status, error);
        }
        static {
            /** mapping of terms to their resolved values */ _Tempo_termMap = { value: new Map() };
        }
        /** @internal Master Guard predicate (implements RegExp-like interface) */ static get [$guard]() { return this[$Internal]()[$guard] ?? { test: () => true }; }
        /** @internal */
        static [$IsBase] = true;
        /** @internal Static access to global private state. */
        static [$Internal]() {
            return ClassStates.get(this) ?? _global;
        }
        /** @internal */
        static get [$ImmutableSkip]() {
            const global = isDefined(globalThis) ? globalThis : window;
            const nodeEnv = isDefined(global.process)
                && global.process.env
                && (global.process.env.NODE_ENV === 'test' || global.process.env.CI);
            return (nodeEnv || global.TEMPO_TESTING)
                ? ['init']
                : [];
        }
        /** @internal brand check to distinguish Tempo objects from other objects */
        get [$Identity]() { return true; }
        /**
         * @internal
         * {dt} is a layout that combines date-related {snippets} (e.g. dd, mm -or- evt) into a pattern against which a string can be tested.
         * because it will also include a list of events (e.g. 'new_years' | 'xmas'), we need to rebuild {dt} if the user adds a new event
         */
        static [$setAliases](shape, kind, token, provided) {
            const field = kind === 'evt' ? 'event' : 'period';
            const parent = proto(shape);
            const parentMap = parent.parse?.[field] ?? {};
            // Identify local additions or overrides
            const entries = provided ?? ownEntries(shape.parse[field], true).filter(([k, v]) => {
                return !(k in parentMap) || shape.parse[field][k] !== parentMap[k];
            });
            // Sync legacy registry if provided directly
            if (provided) {
                if (!hasOwn(shape.parse, field))
                    shape.parse[field] = { ...shape.parse[field] };
                provided.forEach(([k, v]) => {
                    if (!hasOwn(shape.parse[field], k))
                        shape.parse[field][k] = v;
                });
            }
            // If no local aliases, inherit the parent's engine (via prototype) and exit
            if (entries.length === 0 && !hasOwn(shape, 'aliasEngine'))
                return;
            // Use the correct alias engine: static for global, instance for local
            let engine = shape.aliasEngine;
            // If we have local aliases to register, we MUST have a local engine to avoid polluting the parent
            if (!hasOwn(shape, 'aliasEngine')) {
                engine = shape.aliasEngine = new AliasEngine({
                    parent: parent.aliasEngine,
                    config: shape.config
                });
            }
            const groups = engine.registerAliases(kind, entries);
            if (groups) {
                const protoRegex = parent.parse?.snippet?.[token]?.source;
                if (groups !== protoRegex) {
                    if (!hasOwn(shape.parse, 'snippet'))
                        shape.parse.snippet = { ...shape.parse.snippet };
                    setProperty(shape.parse.snippet, token, new RegExp(groups));
                }
            }
            else {
                if (hasOwn(shape.parse.snippet, token)) {
                    if (!hasOwn(shape.parse, 'snippet'))
                        shape.parse.snippet = { ...shape.parse.snippet };
                    delete shape.parse.snippet[token];
                }
            }
        }
        /** @internal */
        static [$setEvents](shape, provided, rebuild = true) {
            this[$setAliases](shape, 'evt', Token.evt, provided);
            if (rebuild)
                setPatterns(shape);
        }
        /** @internal */
        static [$setPeriods](shape, provided, rebuild = true) {
            this[$setAliases](shape, 'per', Token.per, provided);
            if (rebuild)
                setPatterns(shape);
        }
        static {
            /** try to infer hemisphere using the timezone's daylight-savings setting */
            _Tempo_setSphere = { value: (shape, options) => {
                    if (isDefined(options.sphere))
                        return options.sphere;
                    const tz = options.timeZone;
                    if (isDefined(tz)) {
                        const resolvedTz = shape.config.timeZone;
                        if (String(resolvedTz).toLowerCase() === 'utc')
                            return undefined;
                        const sphere = getHemisphere(String(resolvedTz));
                        if (isDefined(sphere))
                            return sphere;
                    }
                    return isDefined(shape.config?.sphere) ? shape.config.sphere : undefined;
                } };
        }
        static {
            /** get first Canonical name of a supplied locale */
            _Tempo_locale = { value: (locale) => {
                    const global = Context.global;
                    let language;
                    const primaryLocale = Array.isArray(locale) ? locale[0] : locale;
                    if (primaryLocale)
                        language = canonicalLocale(primaryLocale);
                    return language ??
                        global?.navigator?.languages?.[0] ?? // fallback to current first navigator.languages[]
                        global?.navigator?.language ?? // else navigator.language
                        Default.locale ?? // else default locale
                        primaryLocale; // cannot determine locale
                } };
        }
        /**
         * conform input of Snippet / Layout / Event / Period options
         * This is needed because we allow the user to flexibly provide detail as {[key]:val} or {[key]:val}[] or [key,val][]
         */
        /** @internal */
        static [(_Tempo_isMonthDay = function _Tempo_isMonthDay(shape) {
            const { timeZone, locale } = shape.config;
            const mdy = shape.parse.monthDay;
            const globalMdy = Tempo.MONTH_DAY;
            let intl;
            try {
                intl = new Intl.Locale(__classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_locale).call(Tempo, locale));
            }
            catch (e) {
                logWarn(`Invalid locale encountered in #isMonthDay: ${locale}. Falling back to en-US.`, shape.config, e);
                intl = new Intl.Locale('en-US');
            }
            const tz = String(timeZone);
            const activeLocaleData = mdy.resolvedLocales?.find(l => l.locale === intl.baseName || l.locale === intl.language);
            return (activeLocaleData?.timeZones?.includes(tz)) ||
                (globalMdy.timezones?.[intl.baseName]?.includes(tz)) ||
                (globalMdy.timezones?.[intl.language]?.includes(tz)) ||
                ((asArray(globalMdy.locales).includes(intl.baseName) || asArray(globalMdy.locales).includes(intl.language)) && (intl.getTimeZones?.() || []).includes(tz));
        }, _Tempo_swapLayout = function _Tempo_swapLayout(shape) {
            const { layouts } = shape.parse.monthDay;
            if (isEmpty(layouts))
                return;
            const isMonthDay = shape.parse.monthDay.isExplicit ? shape.parse.monthDay.active : __classPrivateFieldGet(Tempo, _classThis, "m", _Tempo_isMonthDay).call(Tempo, shape);
            shape.parse.monthDay.active = isMonthDay;
            // ensure Token.dt matches the local monthDay preference
            const dt = isMonthDay ? datePattern.mdy : datePattern.dmy;
            if (shape.parse.layout[Token.dt] !== dt)
                shape.parse.layout = { ...shape.parse.layout, [Token.dt]: dt };
            const layoutController = (shape.parse.planner.layoutOrder?.length ?? 0) > 0
                ? { [DEFAULT_LAYOUT_CLASS]: [...shape.parse.planner.layoutOrder] }
                : undefined;
            const layout = resolveLayoutOrder({
                layout: shape.parse.layout,
                monthDayLayouts: layouts,
                isMonthDay,
                ...(layoutController !== undefined && { layoutController }),
            });
            if (layout !== shape.parse.layout)
                shape.parse.layout = layout;
            logDebug(`Resolved layout order: ${getLayoutOrder(layout).join(' -> ')}`, shape.config);
        }, $setConfig)](shape, ...options) {
            const providedOptions = Object.assign({}, ...options);
            const storeKey = providedOptions.store;
            const mergedOptions = storeKey
                ? Object.assign(Tempo.readStore(storeKey), providedOptions)
                : providedOptions;
            if (isEmpty(mergedOptions))
                return;
            // Apply options using extendState
            const patternsDirty = extendState(shape, mergedOptions);
            let needsRebuild = patternsDirty;
            if (patternsDirty && shape.config.scope === 'local') {
                this[$buildGuard](shape);
            }
            // Side-effects
            const newSphere = __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_setSphere).call(Tempo, shape, mergedOptions);
            if (shape.config.scope === 'local') {
                const parentSphere = Object.getPrototypeOf(shape.config).sphere;
                if (newSphere !== parentSphere)
                    shape.config.sphere = newSphere;
            }
            else {
                shape.config.sphere = newSphere;
            }
            const oldLayout = shape.parse.layout;
            __classPrivateFieldGet(Tempo, _classThis, "m", _Tempo_swapLayout).call(Tempo, shape);
            if (oldLayout !== shape.parse.layout)
                needsRebuild = true;
            if (isDefined(shape.parse.event)) {
                this[$setEvents](shape, undefined, false);
                needsRebuild = true;
            }
            if (isDefined(shape.parse.period)) {
                this[$setPeriods](shape, undefined, false);
                needsRebuild = true;
            }
            if (needsRebuild)
                setPatterns(shape);
        }
        /** support "Global Discovery" of user-options */
        /** @internal */
        static [$setDiscovery](shape, discovery) {
            if (!isObject(discovery))
                return {};
            markConfig(discovery); // auto-mark the discovery object
            const isSandbox = shape !== _global;
            let opts = isFunction(discovery.options) ? discovery.options() : (discovery.options || {});
            // 1. Process TimeZones (normalize to lowercase for lookup)
            if (discovery.timeZones) {
                const tzs = Object.fromEntries(ownEntries(discovery.timeZones, true).map(([k, v]) => [String(k).toLowerCase(), v]));
                if (isSandbox)
                    opts = { ...opts, timeZones: tzs };
                else
                    registryUpdate('TIMEZONE', tzs);
            }
            // 1b. Process Numbers
            if (discovery.numbers) {
                if (isSandbox)
                    opts = { ...opts, numbers: discovery.numbers };
                else
                    registryUpdate('NUMBER', discovery.numbers);
            }
            // 1c. Process MDY settings
            if (discovery.monthDay) {
                let md = discovery.monthDay;
                if (md.timezones) {
                    const zones = Object.fromEntries(ownEntries(md.timezones, true).map(([k, v]) => {
                        try {
                            return [new Intl.Locale(String(k)).baseName, v];
                        }
                        catch {
                            return [String(k), v];
                        }
                    }));
                    if (isSandbox)
                        md = { ...md, timezones: zones };
                    else
                        registryUpdate('MONTH_DAY', { timezones: zones });
                }
                if (!isSandbox) {
                    if (md.locales)
                        registryUpdate('MONTH_DAY', { locales: asArray(md.locales) });
                    if (md.layouts)
                        registryUpdate('MONTH_DAY', { layouts: asArray(md.layouts) });
                }
                opts = { ...opts, monthDay: md };
            }
            // 1d. Process Internationalization
            if (discovery.intl)
                opts = { ...opts, intl: discovery.intl };
            // 1e. Process Planner
            if (isObject(discovery.planner))
                opts = { ...opts, planner: discovery.planner };
            // 2. Process Terms
            if (discovery.terms)
                this.extend(asArray(discovery.terms));
            // 3. Process Registry
            let registryOpts = discovery.registry ?? {};
            if (discovery.formats)
                registryOpts = { ...registryOpts, formats: discovery.formats };
            if (discovery.locales)
                registryOpts = { ...registryOpts, locales: discovery.locales };
            if (Object.keys(registryOpts).length > 0) {
                opts = { ...opts, registry: registryOpts };
                if (!isSandbox) {
                    if (registryOpts.formats)
                        registryUpdate('FORMAT', registryOpts.formats);
                    if (registryOpts.locales)
                        registryUpdate('LOCALE', registryOpts.locales);
                }
            }
            // 4. Process Extensions / Plugins
            if (discovery.plugins && isObject(discovery.plugins) && !Array.isArray(discovery.plugins) && !isFunction(discovery.plugins) && !('name' in discovery.plugins || 'key' in discovery.plugins || 'install' in discovery.plugins))
                opts = { ...opts, plugins: isObject(opts.plugins) ? { ...opts.plugins, ...discovery.plugins } : discovery.plugins };
            // 5. Process Options
            if (discovery.ignore) {
                const ignore = isFunction(discovery.ignore) ? discovery.ignore() : discovery.ignore;
                opts = { ...opts, ignore };
            }
            const res = isFunction(opts) ? opts() : opts;
            if (shape === _global) {
                this[$buildGuard]();
                setPatterns(shape);
            }
            return res;
        }
        /** @internal */
        static [$buildGuard](targetState) {
            const state = targetState ?? this[$Internal]();
            // Note: We MUST use Object.keys() here instead of enums.XXX.keys() because this static guard 
            // executes during module initialization. Circular dependencies mean the enumify methods 
            // (like .keys()) may not be fully attached to the prototype yet!
            const wordsList = [
                ...Object.keys(enums.NUMBER),
                ...Object.keys(enums.WEEKDAY),
                ...Object.keys(enums.WEEKDAYS),
                ...Object.keys(enums.MONTH),
                ...Object.keys(enums.MONTHS),
                ...Object.keys(enums.DURATION),
                ...Object.keys(enums.DURATIONS),
                ...Object.keys(enums.TIMEZONE),
                ...(state.aliasEngine?.getAliases(undefined, true).map((a) => a.name) ?? []),
                ...ownKeys(state.parse.ignore),
                ...ownKeys(state.parse.snippet),
                ...ownKeys(state.parse.layout),
                ...[Token.slk],
                ...__classPrivateFieldGet(Tempo, _classThis, "a", _Tempo_terms_get).map(t => t.key),
                ...__classPrivateFieldGet(Tempo, _classThis, "a", _Tempo_terms_get).map(t => t.scope),
                ...Guard,
                ...(state.config.registry?.modifiers ? Object.values(state.config.registry.modifiers).flat() : [])
            ];
            const guard = createMasterGuard(wordsList);
            if (targetState) {
                targetState.parse.guard = guard;
            }
            else {
                this[$Internal]()[$guard] = guard;
            }
            if (!targetState && this[$Internal]() === _global)
                setPatterns(this[$Internal]());
        }
        static extend(...args) {
            const isOptionsArg = (arg) => isObject(arg) &&
                !isString(arg.name) &&
                !isDefined(arg.key) &&
                !('timeZones' in arg) &&
                !('numbers' in arg) &&
                !('terms' in arg) &&
                !('formats' in arg) &&
                !('locales' in arg) &&
                !('options' in arg);
            let options = (args.length > 1 && isOptionsArg(args[args.length - 1])) ? args.pop() : undefined;
            const licenseKey = options?.license || (args.length === 1 && isObject(args[0]) ? args[0].license : undefined);
            if (licenseKey) {
                const state = this[$Internal]();
                setLicense(state, licenseKey);
                const license = getLicenseState(state);
                if (license.jws?.isPending)
                    validateLicenseState(license, license.jws);
            }
            const items = args.flat(Infinity);
            if (isEmpty(items))
                return this;
            _lifecycle.extendDepth++; // increment the re-entrant nesting counter
            try {
                items.forEach(item => {
                    const arg = item;
                    if (isFunction(arg)) { // Standard Plugin registration
                        const state = this[$Internal]();
                        const rt = getRuntime();
                        const installed = state.installed ?? rt.installed; // ScopedSet for sandboxes, global Set for Tempo
                        if (installed.has(arg))
                            return;
                        installed.add(arg); // mark as installed (BEFORE side-effects)
                        registerPlugin(arg, state);
                        try {
                            arg(this, options, (val) => new this(val));
                        }
                        catch (e) {
                            const msg = (e?.message ?? '').toLowerCase();
                            if (msg.includes('constructor') || msg.includes('class') || (e instanceof TypeError) || isClass(arg)) {
                                logWarn(`Misidentified class in plugin registration: ${arg.name}`, state.config, e.stack ?? e);
                            }
                            else {
                                throw e;
                            }
                        }
                    }
                    else if (isObject(item)) {
                        let pluginType = item[sym.$PluginType];
                        /** @deprecated Legacy structural typing fallback. Will be removed in v4.0.0 */
                        if (!pluginType) {
                            if (isFunction(item.install) && (item.type === 'plugin' || item.type === 'namespace' || item.type === 'module' || isString(item.name))) {
                                pluginType = item.type || 'plugin';
                            }
                            else if (item.type === 'term' || (isString(item.key) && isFunction(item.define))) {
                                pluginType = 'term';
                            }
                        }
                        const assertContract = (isValid, msg) => {
                            if (!isValid) {
                                const config = this[$Internal]().config;
                                if (config.catch) {
                                    logError(msg, config);
                                    return false;
                                }
                                throw new TypeError(msg);
                            }
                            return true;
                        };
                        switch (pluginType) {
                            case 'plugin':
                            case 'namespace':
                            case 'module': {
                                if (!assertContract(isFunction(item.install), `[Tempo] Invalid ${pluginType} object: missing 'install()' method.`))
                                    return;
                                const name = item.name;
                                const state = this[$Internal]();
                                const rt = getRuntime();
                                const installed = state.installed ?? rt.installed; // ScopedSet for sandboxes, global Set for Tempo
                                if (installed.has(name)) {
                                    logDebug(`Plugin already installed by name: ${name}`, state.config);
                                    return;
                                }
                                installed.add(name);
                                registerPlugin(item, state);
                                if (item.version && isString(name)) {
                                    let suffix = '';
                                    if (pluginType === 'plugin')
                                        suffix = 'Plugin';
                                    else if (pluginType === 'namespace')
                                        suffix = 'Namespace';
                                    else if (pluginType === 'module')
                                        suffix = 'Module';
                                    else
                                        suffix = 'Plugin';
                                    __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_versions)[`${name}${name.endsWith(suffix) ? '' : suffix}`] = item.version;
                                }
                                item.install.call(this, this, options);
                                break;
                            }
                            case 'term': {
                                if (!assertContract(isFunction(item.define), `[Tempo] Invalid term object: missing 'define()' method.`))
                                    return;
                                const config = item;
                                const state = this[$Internal]();
                                if (__classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).get(config.key) === config)
                                    return;
                                if (__classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).has(config.key)) {
                                    const existing = __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).get(config.key);
                                    const rangesMatch = JSON.stringify(existing?.ranges) === JSON.stringify(config.ranges);
                                    if (existing?.scope === config.scope && existing?.description === config.description && rangesMatch) {
                                        logDebug(`[Tempo#extend] Duplicate term registration ignored for key: "${config.key}"`, state.config);
                                        return;
                                    }
                                    logError(`[Tempo#extend] Term collision on key: "${config.key}". Registration aborted.`, state.config);
                                    return;
                                }
                                if (config.scope && __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).get(config.scope) === config) { /* continue */ }
                                else if (config.scope && __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).has(config.scope)) {
                                    const existingScope = __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).get(config.scope);
                                    const rangesMatch = JSON.stringify(existingScope?.ranges) === JSON.stringify(config.ranges);
                                    if (existingScope?.key === config.key && existingScope?.description === config.description && rangesMatch) {
                                        /* continue */
                                    }
                                    else {
                                        logError(`[Tempo#extend] Term collision on scope: "${config.scope}". Registration aborted.`, state.config);
                                        return;
                                    }
                                }
                                __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).set(config.key, config);
                                if (config.scope)
                                    __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).set(config.scope, config);
                                if (config.version) {
                                    const name = config.scope || config.key;
                                    __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_versions)[`${name}Term`] = config.version;
                                }
                                registerTerm(config, this[$Internal]());
                                // 1a. sync with alias engine
                                if (config.scope && config.ranges) {
                                    const type = config.scope === 'period' ? 'per' : (config.scope === 'event' ? 'evt' : undefined);
                                    if (type) {
                                        const aliases = [];
                                        const monthKeys = Tempo.MONTH.keys();
                                        config.ranges.forEach(r => {
                                            if (r.key) {
                                                let val;
                                                if (isDefined(r.hour)) {
                                                    if (Number.isInteger(r.hour) && r.hour >= 0 && r.hour <= 23)
                                                        val = `${r.hour}:${pad(r.minute ?? 0)}`;
                                                }
                                                else if (r.month) {
                                                    if (Number.isInteger(r.month) && r.month >= 1 && r.month <= 12)
                                                        val = `${pad(r.day ?? 1)} ${monthKeys[r.month - 1]}`;
                                                }
                                                if (val)
                                                    aliases.push([r.key, val]);
                                            }
                                        });
                                        if (aliases.length > 0) {
                                            const state = this[$Internal]();
                                            if (type === 'per')
                                                this[$setPeriods](state, aliases);
                                            else if (type === 'evt')
                                                this[$setEvents](state, aliases);
                                        }
                                    }
                                }
                                break;
                            }
                            default: {
                                // Safe fallback to Discovery Object parsing
                                const discovery = item;
                                const opts = this[$setDiscovery](this[$Internal](), discovery);
                                if (!isEmpty(opts))
                                    this[$setConfig](this[$Internal](), opts);
                                if (discovery.extends)
                                    asArray(discovery.extends).forEach(p => this.extend(p));
                                else if (discovery.plugins && (Array.isArray(discovery.plugins) || isFunction(discovery.plugins) || (isObject(discovery.plugins) && ('name' in discovery.plugins || 'key' in discovery.plugins || 'install' in discovery.plugins))))
                                    asArray(discovery.plugins).forEach(p => this.extend(p));
                                // only trigger init if we're assigning a new discovery object to a symbol
                                if (ownKeys(item).some(key => DISCOVERY.has(key))) {
                                    const discoveryArg = (isSymbol(options) ? options : options?.discovery) ?? sym.$Tempo;
                                    const discoverySymbol = isString(discoveryArg) ? Symbol.for(discoveryArg) : (isSymbol(discoveryArg) && !Symbol.keyFor(discoveryArg) ? Symbol('TempoSandbox') : discoveryArg);
                                    if (globalThis[discoverySymbol] !== item) {
                                        globalThis[discoverySymbol] = item;
                                        this[$setConfig](this[$Internal](), { discovery: discoverySymbol });
                                    }
                                }
                                break;
                            }
                        }
                    }
                });
            }
            finally {
                _lifecycle.extendDepth--; // decrement the re-entrant nesting counter
            }
            if (_lifecycle.extendDepth === 0) {
                this[$buildGuard]();
                setPatterns(this[$Internal]()); // rebuild the global patterns
            }
            return this;
        }
        /**
         * 🏭 Sandbox Factory Mode
         * Create a fresh, isolated Tempo subclass with its own configuration and registries.
         * Returns a new class that inherits from the caller, but maintains independent state.
         */
        static create(options = {}) {
            const SandboxTempo = class extends this {
                static [Symbol.toStringTag] = 'TempoSandbox';
                static [$IsBase] = false;
            };
            const discovery = options.discovery;
            const normalizedDiscovery = (isObject(discovery) && !isSymbol(discovery)) || isUndefined(discovery) || (isSymbol(discovery) && !Symbol.keyFor(discovery))
                ? Symbol('TempoSandbox')
                : (isString(discovery) ? Symbol.for(discovery) : discovery);
            let data = { options: { ...options, discovery: normalizedDiscovery }, scope: 'sandbox' };
            if (isObject(options.discovery) && !isSymbol(options.discovery))
                data = { ...data, ...options.discovery };
            globalThis[normalizedDiscovery] = data;
            const state = init(options, false, this[$Internal]());
            if (options?.license)
                setLicense(state, options.license);
            state.config.discovery = normalizedDiscovery;
            ClassStates.set(SandboxTempo, state);
            setPatterns(state); // compile regex patterns for the isolated sandbox state
            // Apply configuration to the sandbox
            SandboxTempo[$setConfig](state, {
                scope: 'sandbox',
                discovery: normalizedDiscovery,
                catch: options.catch ?? false
            }, SandboxTempo[$setDiscovery](state, data), { ...options, discovery: normalizedDiscovery });
            if (data?.extends)
                SandboxTempo.extend(data.extends);
            else if (data?.plugins && (Array.isArray(data.plugins) || isFunction(data.plugins) || (isObject(data.plugins) && ('name' in data.plugins || 'key' in data.plugins || 'install' in data.plugins))))
                SandboxTempo.extend(data.plugins);
            Object.freeze(SandboxTempo);
            // 🏛️ Trigger background license validation for sandbox-local license keys
            // (mirrors the same reckoning logic in Tempo.init() for the global license)
            const sandboxLicense = getLicenseState(state);
            if (sandboxLicense.jws?.isPending)
                validateLicenseState(sandboxLicense, sandboxLicense.jws);
            return SandboxTempo;
        }
        /**
         * Wait for the background licensing and validation engine to finish settling.
         * Resolves immediately if no license key or validation is pending.
         * @returns The final, human-readable license status ('none', 'active', 'expired', etc.)
         */
        static async ready() {
            const rt = getRuntime();
            const jws = rt.license?.jws;
            if (jws) {
                try {
                    await jws;
                }
                catch { /* fail-safe */ }
            }
            return resolveDisplayStatus(rt.license.status);
        }
        /**
         * Automatically discovers and loads configuration from `tempo.config.*`
         * before initializing the engine.
         */
        static async bootstrap(options) {
            const { resolveConfig } = await import('./config/config.resolve.js');
            const config = await resolveConfig(options);
            this.init(config || {});
            await this.ready();
            return this;
        }
        /** Reset Tempo to its default, built-in registration state */
        static init(options = {}) {
            if (_lifecycle.initialising)
                return this;
            _lifecycle.initialising = true;
            try {
                setLogLevel(options.debug ?? Default?.debug ?? LOG.Info);
                const rt = getRuntime();
                const prevCache = rt.state?.cache;
                const isBase = !!this[$IsBase];
                if (isBase)
                    rt.state = undefined;
                const baseState = isBase ? undefined : Object.getPrototypeOf(this)[$Internal]();
                const state = init(options, isBase, baseState, prevCache);
                state._count = 0;
                if (isBase) {
                    _global = state;
                }
                else {
                    ClassStates.set(this, state);
                }
                // 1. Augment the parsing state (non-destructively)
                const parse = state.parse;
                parse.pattern ??= new Map();
                parse.monthDay = resolveMonthDay(Default.monthDay, Tempo.MONTH_DAY);
                parse.planner.layoutOrder = asArray(Default.planner?.layoutOrder ?? Default.layoutOrder);
                parse.planner.preFilter = Boolean(Default.planner?.preFilter ?? Default.preFilter);
                parse.pivot ??= Default.pivot;
                parse.mode ??= Default.mode;
                parse.lazy = false;
                // 2. Establish context and keys
                const sys = getDateTimeFormat();
                const timeZone = options.timeZone ?? sys.timeZone;
                const calendar = options.calendar ?? sys.calendar;
                const config = state.config;
                let discovery = options.discovery ?? Symbol.keyFor($Tempo);
                const storeKey = options.store || config.store || Symbol.keyFor($Tempo);
                // Normalize discovery to a symbol if it's an object to prevent leakage into config state
                if (isObject(discovery) && !isSymbol(discovery)) {
                    const data = discovery;
                    discovery = Symbol.for(`tempo.discovery.${_usrCount++}`);
                    globalThis[discovery] = data;
                }
                const normalizedDiscovery = isString(discovery) ? Symbol.for(discovery) : discovery;
                const userDiscovery = globalThis[normalizedDiscovery];
                // Resolve locale if missing or invalid
                const currentLocale = config.locale;
                const locale = (!currentLocale || currentLocale === 'en-US') ? __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_locale).call(Tempo, currentLocale) : currentLocale;
                if (!hasOwn(config, 'get')) {
                    Object.defineProperty(config, 'get', {
                        value: function (key) { return this[key]; },
                        enumerable: false, writable: true, configurable: true
                    });
                }
                _usrCount = 0; // reset user-key counter
                for (const key of Object.keys(Token)) // purge user-allocated Tokens
                    if (key.startsWith('usr.')) // only remove 'usr.' prefixed keys
                        delete Token[key];
                __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).clear(); // clear term lookup map
                registryReset(); // purge formats and numbers
                // 3. Apply configuration via unified setters (non-destructive merge)
                this[$setConfig](state, {
                    calendar,
                    timeZone,
                    locale,
                    discovery: normalizedDiscovery,
                    registry: {
                        formats: config.registry?.formats ?? config.formats ?? enumify(STATE.FORMAT, false),
                        locales: config.registry?.locales ?? config.locales ?? proxify(STATE.LOCALE, true, true)
                    },
                    scope: 'global',
                    catch: options.catch ?? config.catch ?? false
                }, { store: storeKey, discovery: normalizedDiscovery, scope: 'global' }, this.readStore(storeKey), // allow for storage-values to overwrite
                this[$setDiscovery](state, state.pluginsDb), // persistent library extensions
                this[$setDiscovery](state, userDiscovery), // user Discovery (Configuration bootstrapping)
                options);
                setLogLevel(state.config.debug ?? options.debug ?? Default?.debug ?? LOG.Info);
                if (userDiscovery?.extends)
                    this.extend(userDiscovery.extends);
                if (options.extends)
                    this.extend(options.extends);
                // TODO: @deprecated - Remove in Tempo v4.0.0
                if (userDiscovery?.plugins && (Array.isArray(userDiscovery.plugins) || isFunction(userDiscovery.plugins) || (isObject(userDiscovery.plugins) && ('name' in userDiscovery.plugins || 'key' in userDiscovery.plugins || 'install' in userDiscovery.plugins))))
                    this.extend(userDiscovery.plugins);
                if (options.plugins) {
                    if (Array.isArray(options.plugins) || isFunction(options.plugins) || (isObject(options.plugins) && ('name' in options.plugins || 'key' in options.plugins || 'install' in options.plugins)))
                        this.extend(options.plugins);
                }
                if (Context.type === CONTEXT.Browser || state.config.debug === LOG.Debug)
                    logDebug('Tempo:', this.config, state.config);
                setPatterns(state); // rebuild the global patterns (Master Guard etc)
                // 🏛️ Licensing Reckoning (Background Verification)
                if (rt.license.jws?.isPending)
                    validateLicenseState(rt.license, rt.license.jws);
                _lifecycle.ready = true;
                return this;
            }
            catch (err) {
                _lifecycle.ready = false;
                throw err;
            }
            finally {
                _lifecycle.initialising = false;
                _lifecycle.bootstrap = false;
            }
        }
        /** explicitly enable/disable "catch" mode for internal errors */
        static setCatchMode(catchMode) {
            this[$setConfig](this[$Internal](), { catch: catchMode });
            return this;
        }
        /** @internal unload a module by name (experimental internal use only) */
        static unloadModule(name) {
            const rt = getRuntime();
            delete rt.modules[name];
            rt.installed.delete(name);
            const baseName = name.endsWith('Module') ? name.slice(0, -6) : name;
            rt.installed.delete(baseName);
            return this;
        }
        /** @internal check if a module is loaded */
        static hasModule(name) {
            const rt = getRuntime();
            const mod = name === 'term' ? 'TermsModule' : name;
            return isDefined(rt.modules[mod]) || rt.installed.has(mod) || this[$Internal]().pluginsDb.plugins.some(p => p.name === mod);
        }
        /** @internal Reads options from persistent storage (e.g., localStorage). */
        static readStore(key = this[$Internal]().config.store) {
            return getStorage(key, {});
        }
        /** @internal Writes configuration into persistent storage. */
        static writeStore(config, key = this[$Internal]().config.store) {
            return setStorage(key, config);
        }
        /** @internal lookup or registers a new `Symbol` for a given key. */
        static getSymbol(key) {
            if (isUndefined(key)) {
                const usr = `usr.${++_usrCount}`; // allocate a prefixed 'user' key
                return Token[usr] = Symbol(usr); // add to Symbol register
            }
            if (isSymbol(key))
                return key;
            if (isString(key) && key.includes('.')) {
                const description = key.split('.').pop(); // use last segment as description
                return Token[key] ??= Symbol(description);
            }
            return Token[key] ?? Symbol.for(`$Tempo.${key}`);
        }
        /** translates {layout} into an anchored, case-insensitive RegExp. */
        static regexp(layout, snippet) {
            const state = this[$Internal]();
            if (!state.patternCompiler || state.patternCompiler.state !== state)
                state.patternCompiler = new PatternCompiler({ state });
            return state.patternCompiler.compileRegExp(layout, snippet);
        }
        /** Compares two `Tempo` instances or date-time values. Returns -1 (earlier), 0 (equal), or 1 (later). */
        static compare(tempo1, tempo2) {
            const host = (this && this[$Identity]) ? this : Tempo;
            const one = host.isTempo(tempo1) ? tempo1 : new host(tempo1), two = host.isTempo(tempo2) ? tempo2 : new host(tempo2);
            return Number((one.nano > two.nano) || -(one.nano < two.nano)) + 0;
        }
        /** global Tempo configuration */
        static get config() {
            const state = this[$Internal]();
            // Create an object that only contains CONFIG-specific defaults as its prototype,
            // preventing parse-related keys (like planner, monthDay) from leaking into the config state.
            const configDefaults = Object.fromEntries(Object.entries(Default).filter(([key]) => enums.CONFIG.has(key)));
            const out = Object.create(configDefaults);
            const descriptors = omit(Object.getOwnPropertyDescriptors(state.config), 'value', 'anchor', 'result');
            Object.defineProperties(out, descriptors);
            Object.defineProperty(out, 'toJSON', // bare-bones: only show global overrides
            {
                value: () => Object.fromEntries(Object.entries(out)), // proxify sees own toJSON, skips allObject
                enumerable: false, configurable: true
            });
            Object.defineProperty(out, sym.$Inspect, {
                value: () => Object.fromEntries(Object.entries(out)),
                enumerable: false, configurable: true
            });
            return proxify(out);
        }
        /** global discovery configuration */
        static get discovery() {
            const discovery = this.config.discovery;
            const sym = isString(discovery) ? Symbol.for(discovery) : discovery;
            return __classPrivateFieldGet(Tempo, _classThis, "m", _Tempo_getConfig).call(Tempo, sym);
        }
        static get options() {
            const keyFor = this.config.store ?? Symbol.keyFor($Tempo);
            const storage = proxify(Object.assign({ key: keyFor, scope: 'storage' }, omit(Tempo.readStore(keyFor), 'value')));
            return Object.assign({}, this.default, storage, this.discovery, this.config);
        }
        static from(tempo, options) { return new this(tempo, options); }
        static now(unit) {
            const inst = instant();
            switch (unit) {
                case 'ss': return Math.trunc(inst.epochMilliseconds / 1_000);
                case 'ms': return inst.epochMilliseconds;
                case 'us': return Number(inst.epochNanoseconds / BigInt(1_000));
                case 'ns':
                default: return inst.epochNanoseconds;
            }
        }
        /** static units since Unix epoch */
        static get epoch() {
            return __classPrivateFieldGet(Tempo, _classThis, "m", _Tempo_getEpoch).call(Tempo, instant());
        }
        /** get the current system Instant */
        static get instant() { return Temporal.Instant.fromEpochNanoseconds(Tempo.now()); }
        /** static Tempo.terms (registry) */
        static get terms() {
            const rt = getRuntime();
            const list = __classPrivateFieldGet(Tempo, _classThis, "a", _Tempo_terms_get).map(({ define, resolve, ...rest }) => {
                const item = { ...rest };
                if (hasOwn(rt.license.scopes, rest.key)) {
                    const meta = rt.license.scopes[rest.key];
                    item.status = resolveDisplayStatus(rt.license.status);
                    item.expires = meta.exp ?? rt.license.expires;
                    if (meta.updated_at)
                        item.updated = meta.updated_at;
                }
                return item;
            });
            // 📋 Synthetic stubs: surface JWT-claimed scopes that haven't been registered yet
            const registeredKeys = new Set(list.map((t) => t.key));
            const licenseStatus = rt.license.status;
            if (licenseStatus && licenseStatus !== 'none') {
                for (const [scopeKey, meta] of Object.entries(rt.license.scopes || {})) {
                    if (!registeredKeys.has(scopeKey)) {
                        const m = meta;
                        list.push({
                            key: scopeKey,
                            description: `Premium plugin (uninstalled)`,
                            status: resolveDisplayStatus(licenseStatus),
                            expires: m?.exp ?? rt.license.expires,
                            ...(m?.updated_at && { updated: m.updated_at }),
                        });
                    }
                }
            }
            // treats `Tempo.terms` as array-like and indexable by key.
            return indexedArray(list, key => list.find((t) => t.key === key || t.scope === key));
        }
        /** static Tempo.registry */
        static get registry() {
            return Tempo.config.registry;
        }
        /** @deprecated Use Tempo.registry.formats instead */
        static get formats() {
            // Extend built-in enums.FORMAT with any user-registered formats, preserving Enum methods (.keys(), .values(), etc.)
            const userFormats = Tempo.config.registry?.formats;
            return (userFormats && Object.keys(userFormats).length > 0)
                ? enums.FORMAT.extend(userFormats, false)
                : enums.FORMAT;
        }
        /** static Tempo properties getter */
        static get properties() {
            return secure(getAccessors(Tempo)
                .filter(acc => getType(acc) !== 'Symbol')); // omit any Symbol properties
        }
        /** Tempo initial default settings */
        static get default() {
            return Object.freeze({ ...Default, scope: 'default', timeZone: Default.timeZone || enums.TIMEZONE.utc });
        }
        /**
         * configuration governing the static 'rules' used when parsing t.DateTime argument
         */
        static get parse() {
            const parse = this[$Internal]().parse;
            const planner = {
                layoutOrder: [...(parse.planner?.layoutOrder ?? [])],
                preFilter: parse.planner?.preFilter ?? false
            };
            return secure({
                ...omit({ ...parse }, 'token', 'planner'), // spread primitives like {pivot}
                snippet: { ...parse.snippet }, // spread nested objects
                layout: { ...parse.layout },
                event: { ...parse.event },
                period: { ...parse.period },
                ignore: { ...parse.ignore },
                monthDay: clone(parse.monthDay),
                planner,
            });
        }
        /** iterate over Tempo properties */
        static [(_Tempo_getConfig = function _Tempo_getConfig(sym) {
            const discovery = globalThis[sym];
            return proxify(omit({ ...discovery, scope: 'discovery' }, 'value'));
        }, _Tempo_getEpoch = function _Tempo_getEpoch(inst) {
            return secure({
                /** seconds since epoch */ ss: Math.trunc(inst.epochMilliseconds / 1_000),
                /** milliseconds since epoch */ ms: inst.epochMilliseconds,
                /** microseconds since epoch */ us: Number(inst.epochNanoseconds / BigInt(1_000)),
                /** nanoseconds since epoch */ ns: inst.epochNanoseconds,
            });
        }, Symbol.iterator)]() {
            return Tempo.properties[Symbol.iterator](); // static Iterator over array of 'getters'
        }
        /** release global config and reset library to defaults */
        static [Symbol.dispose]() { Tempo.init(); }
        /** static Tempo.ignores (registry) */
        static get ignores() {
            return secure(ownKeys(this[$Internal]().parse.ignore, true));
        }
        /** allow instanceof to work across module boundaries via the local brand symbol */
        /** @internal */
        static [$Identity] = true;
        static [Symbol.hasInstance](instance) {
            return isDefined(instance?.[$Identity]);
        }
        /** check if a supplied variable is a valid Tempo instance */
        static isTempo(instance) {
            return instance instanceof Tempo;
        }
        static {
            // Define the reactive register hook
            getRuntime().setHook($Register, (plugin) => {
                if (!Tempo.isExtending)
                    Tempo.extend(plugin);
            });
            onRegistryReset(() => {
                const state = Tempo[sym.$Internal]();
                // 🏛️ Clear the root engine to avoid stale state and collision warnings during hard resets
                if (state.aliasEngine && state.aliasEngine.depth === 0) {
                    state.aliasEngine.clear('evt');
                    state.aliasEngine.clear('per');
                }
                Tempo[$buildGuard]();
                Tempo[$setEvents](state, undefined, false);
                Tempo[$setPeriods](state, undefined, false);
                setPatterns(state);
            });
            Tempo.init(); // synchronously initialize the library
            getRuntime().logger = logTempo;
        }
        /** constructor tempo */ #tempo;
        /** constructor options */ #options = {};
        /** instantiation Temporal Instant */ #instant;
        /** underlying Temporal ZonedDateTime */ #zdt;
        /** memoized TimeZone ID */ #tz;
        /** memoized Calendar ID */ #cal;
        /** indicator that the instance failed to parse */ #errored = false;
        /** temporary anchor used during parsing */ #anchor;
        /** prebuilt formats, for convenience */ #fmt;
        /** mapping of terms to their resolved values */ #term;
        /** a collection of parse rule-matches */ #matches;
        /** current parsing depth to manage state isolation */ #parseDepth = 0;
        /** current mutation depth to manage infinite recursion */ #mutateDepth = 0;
        /** instance values to complement static values */ #local = {
            /** instance configuration */ config: { [$LogConfig]: true },
            /** instance parse rules (only populated if provided) */ parse: { result: [] }
        };
        /** @internal internal key for signaling pre-errored state in constructor */
        static [$errored] = $errored;
        /** @internal */ static [TermError](config, term) {
            const hint = __classPrivateFieldGet(Tempo, _classThis, "a", _Tempo_terms_get).length === 0 ? ". (No term plugins are registered—did you forget to call Tempo.extend(TermsModule)?)" : "";
            const msg = `Unknown Term identifier: ${term}${hint}`;
            logError(msg, config);
        }
        /**
         * @internal Internal access to instance private state.
         * This surface is not part of the public contract and is subject to change.
         */
        [$Internal]() {
            const self = unwrap(this);
            const out = {
                get zdt() { return self.#zdt; },
                set zdt(val) { self.#zdt = val; },
                get errored() { return self.#errored; },
                set errored(val) { self.#errored = val; },
                get parseDepth() { return self.#parseDepth; },
                set parseDepth(val) { self.#parseDepth = val; },
                get mutateDepth() { return self.#mutateDepth; },
                set mutateDepth(val) { self.#mutateDepth = val; },
                get matches() { return self.#matches; },
                set matches(val) { self.#matches = val; },
                get anchor() { return self.#anchor; },
                set anchor(val) { self.#anchor = val; },
                get options() { return self.#options; },
                get tempo() { return self.#tempo; },
                get now() { return self.#now; },
                config: self.#local.config,
                parse: self.#local.parse,
                userProvidedKeys: self.#local.userProvidedKeys,
                pluginsDb: self.#local.pluginsDb,
                aliasEngine: self.#local.aliasEngine,
                _id: self.#local._id,
                tempoInstance: self,
                CONFIG: enums.CONFIG,
                PARSE: enums.PARSE,
                ZONED_DATE_TIME: enums.ZONED_DATE_TIME
            };
            return Object.setPrototypeOf(out, self.#local);
        }
        /** allow for auto-convert of Tempo to BigInt, Number or String */
        [Symbol.toPrimitive](hint) {
            switch (hint) {
                case 'string': return this.toString(); // iso 8601 string
                case 'number': return this.epoch.ms; // Unix epoch (milliseconds)
                default: return this.nano; // Unix epoch (nanoseconds)
            }
        }
        /** iterate over instance formats */
        [Symbol.iterator]() {
            return ownEntries(this.fmt, true)[Symbol.iterator](); // instance Iterator over tuple of FormatType[]
        }
        get [Symbol.toStringTag]() {
            return 'Tempo'; // hard-coded to avoid minification mangling
        }
        constructor(tempo, options = {}) {
            [this.#tempo, this.#options] = this.#swap(tempo, options); // swap arguments around
            if (isZonedDateTime(this.#tempo))
                this.#zdt = this.#tempo;
            this.#setLocal(this.#options); // parse local options
            if (!this.#zdt && isObject(this.#tempo) && isDurationLike(this.#tempo)) {
                // relative shorthand for "now plus duration"
                this.#zdt = this.#now.toZonedDateTimeISO(this.#local.config.timeZone).add(this.#tempo);
            }
            const { mode } = this.#local.parse;
            const input = String(this.#tempo ?? '');
            const guard = this.#local.parse.guard || this.constructor[$guard];
            // 🏛️ Initialization Strategy ('auto' | 'strict' | 'defer')
            if (mode === Tempo.MODE.Defer)
                this.#local.parse.lazy = true;
            else if (mode === Tempo.MODE.Strict)
                this.#local.parse.lazy = false;
            else if (isString(this.#tempo) && !isEmpty(input) && guard.test(trimAll(input)))
                this.#local.parse.lazy = true; // auto-switch to lazy-mode for valid strings
            this.#anchor = this.#options.anchor;
            // 🧬 Unified State Hand-off (from clone / mutate)
            const handoff = this.#options[$Internal];
            if (isObject(handoff)) {
                this.#errored = handoff.errored ?? false;
                this.#mutateDepth = handoff.mutateDepth ?? 0;
                this.#parseDepth = 0;
                this.#matches = handoff.matches;
            }
            else if (this.#options[$errored]) {
                this.#errored = true;
            }
            if (!this.#local.parse.lazy)
                this.#resolve(); // attempt to interpret immediately (if not lazy)
            return Object.freeze(this);
        }
        /** explicitly enable/disable "catch" mode for this instance */
        setCatchMode(catchMode) {
            setProperty(this.#local.config, 'catch', catchMode);
            return this;
        }
        /** @internal unload a module by name (experimental internal use only) */
        unloadModule(name) {
            this.constructor.unloadModule(name);
            return this;
        }
        /** @internal check if a module is loaded */
        hasModule(name) {
            return this.constructor.hasModule(name);
        }
        /** returns a [timezone, calendar] tuple derived from the underlying date-time. */
        #temporalIds() {
            if (!this.#tz || !this.#cal) {
                [this.#tz, this.#cal] = getTemporalIds(this.toDateTime());
            }
            return [this.#tz, this.#cal];
        }
        /** Resolve the instance to a Temporal.ZonedDateTime (with optional callback) */
        #resolve(cb) {
            if (!this.#zdt) {
                try {
                    const skip = [this.#local.parse.format, this.#local.parse.term, this.#local.parse.result]
                        .filter(isDefined);
                    this.#zdt = this.#parse(this.#tempo, this.#anchor);
                    if (isUndefined(this.#zdt)) {
                        this.#errored = true;
                        const msg = `Tempo parse returned undefined for: ${String(this.#tempo)}`;
                        logError(msg, this.#local.config);
                        this.#zdt = this.#now.toZonedDateTimeISO('UTC');
                    }
                    secure(this.#local.config);
                    secure(this.#local.parse, new WeakSet(skip));
                }
                catch (err) {
                    this.#errored = true; // mark as errored
                    const msg = `Cannot create Tempo: ${err.message}\n${err.stack}`;
                    if (this.#local.config.catch === true) {
                        logError(msg, this.#local.config); // log as error if in catch-mode
                        this.#zdt = this.#now.toZonedDateTimeISO('UTC');
                    }
                    else {
                        logError(err, this.#local.config, msg); // log as error then re-throw
                        throw err;
                    }
                }
            }
            const zdt = isZonedDateTime(this.#zdt) ? this.#zdt : this.#now.toZonedDateTimeISO('UTC');
            return cb?.(zdt) ?? zdt;
        }
        #setLazy(target, name, define, isKeyOnly = false) {
            if (isDefined(name) && isDefined(define)) {
                const desc = Object.getOwnPropertyDescriptor(target, name);
                if (desc)
                    return ('value' in desc) ? () => desc.value : desc.get;
                let guard = false;
                let memo;
                let set = false;
                const get = () => {
                    if (guard)
                        return undefined; // recursion guard
                    guard = true;
                    try {
                        if (!set) {
                            try {
                                memo = define.call(this, isKeyOnly); // evaluate the property
                            }
                            catch (e) {
                                const msg = (e?.message ?? '').toLowerCase();
                                if (msg.includes('constructor') || msg.includes('class') || (e instanceof TypeError) || isClass(define)) {
                                    logWarn(`Misidentified class in delegator evaluate: ${String(define)}`, this.#local.config, e.stack ?? e);
                                    memo = define;
                                }
                                else {
                                    throw e;
                                }
                            }
                            set = true;
                            // Promote to own property on target for subsequent calls (memoization) if extensible
                            if (Reflect.isExtensible(target))
                                Object.defineProperty(target, name, { value: memo, enumerable: true, configurable: true, writable: false });
                        }
                        return memo;
                    }
                    finally {
                        guard = false;
                    }
                };
                // shadowing chain (only if extensible)
                if (Reflect.isExtensible(target))
                    Object.defineProperty(target, name, { get, enumerable: true, configurable: true });
                return get; // return getter closure
            }
            return undefined;
        }
        /** create a Proxy-based delegator that registers lazy properties on-demand */
        #setDelegator(host) {
            const target = Object.create(null);
            const proxy = delegate(target, (key) => {
                if (key === $Discover)
                    return this.#discover(host, target);
                if (!isString(key))
                    return;
                // discovery phase
                if (host === 'fmt') {
                    if (!ensureModule(this, 'FormatModule'))
                        return undefined;
                    const allFormats = Object.assign({}, enums.FORMAT, this.#local.config.registry?.formats);
                    if (isDefined(allFormats[key]))
                        return this.#setLazy(target, key, () => this.format(key))?.();
                }
                else {
                    if (!ensureModule(this, 'TermsModule'))
                        return undefined;
                    // 🛡️ Lazy Proxy Guard (Licensing)
                    if (this.#isBlocked(key))
                        return undefined;
                    const term = __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_termMap).get(key);
                    if (term) {
                        const isKeyOnly = term.key === key;
                        const define = (keyOnly) => {
                            // 🛡️ Resolution Guard (Licensing)
                            // We check again to handle the transition from Pending -> Revoked/Expired
                            if (getRuntime().license.status !== LICENSE.Active && this.#isBlocked(key))
                                return undefined;
                            try {
                                const result = term.define.call(this, keyOnly);
                                const res = Array.isArray(result) ? getTermRange(this, result, keyOnly) : result;
                                return isObject(res) ? secure(res) : res;
                            }
                            catch (err) {
                                const error = asError(err);
                                if (error.message.includes('Class constructor')) {
                                    logWarn(`Misidentified class in term definition: ${key}`, this.#local.config, error.stack ?? error);
                                }
                                else {
                                    throw error;
                                }
                            }
                            return undefined;
                        };
                        return this.#setLazy(target, key, define, isKeyOnly)?.();
                    }
                }
            }, true);
            // Eager support during construction
            if (!this.#local.parse.lazy)
                this.#discover(host, target);
            return proxy;
        }
        #discover(host, target) {
            if (!_lifecycle.ready)
                return;
            if (host === 'fmt') {
                const allFormats = Object.assign({}, enums.FORMAT, this.#local.config.registry?.formats);
                ownKeys(allFormats).forEach(key => {
                    if (isString(key))
                        this.#setLazy(target, key, () => this.format(key));
                });
            }
            else {
                __classPrivateFieldGet(Tempo, _classThis, "a", _Tempo_terms_get).forEach(term => {
                    const define = (keyOnly, anchor) => {
                        // 🛡️ Resolution Guard (Licensing)
                        if (getRuntime().license.status !== LICENSE.Active && this.#isBlocked(term.key))
                            return undefined;
                        try {
                            const res = term.resolve ? term.resolve.call(this, anchor) : term.define.call(this, keyOnly, anchor);
                            const out = getTermRange(this, (Array.isArray(res) ? res : [res]), keyOnly, anchor);
                            return isObject(out) ? secure(out) : out;
                        }
                        catch (err) {
                            const error = asError(err);
                            if (error.message.includes('Class constructor')) {
                                logWarn(`Misidentified class in term discovery: ${term.key}`, this.#local.config, error.stack ?? error);
                            }
                            else {
                                throw error;
                            }
                        }
                    };
                    // If the term has a scope, register key as shortform (isKeyOnly=true → returns key string)
                    // and scope as longform (isKeyOnly=false → returns full Range with label).
                    // If the term has NO scope, the key IS the only accessor — register it as longform
                    // (isKeyOnly=false) so format() can access res.label on the returned Range.
                    this.#setLazy(target, term.key, (isKey) => define(isKey, this.toDateTime()), !!term.scope);
                    if (term.scope)
                        this.#setLazy(target, term.scope, (isKey) => define(isKey, this.toDateTime()), false);
                });
            }
        }
        /** 4-digit year (e.g., 2024) */ get yy() { return this.toDateTime().year; }
        /** Era string for the calendar (e.g., 'ce', 'bce') */ get era() { return this.toDateTime().era; }
        /** Year within the era (positive integer) */ get eon() { return this.toDateTime().eraYear; }
        /** @hidden */ get eraYear() { return this.toDateTime().eraYear; }
        /** 4-digit iso week-numbering year */ get yw() { return getISOWeekOfYear(this.toDateTime()).yearOfWeek; }
        /** Month number: Jan=1, Dec=12 */ get mm() { return this.toDateTime().month; }
        /** iso week number of the year */ get wy() { return getISOWeekOfYear(this.toDateTime()).weekOfYear; }
        /** @deprecated use `wy` */ get ww() { return getISOWeekOfYear(this.toDateTime()).weekOfYear; }
        /** Day of the month (1-31) */ get dd() { return this.toDateTime().day; }
        /** Day of the month (alias for `dd`) */ get day() { return this.toDateTime().day; }
        /** Hour of the day (0-23) */ get hh() { return this.toDateTime().hour; }
        /** Minutes of the hour (0-59) */ get mi() { return this.toDateTime().minute; }
        /** Seconds of the minute (0-59) */ get ss() { return this.toDateTime().second; }
        /** Milliseconds of the second (0-999) */ get ms() { return this.toDateTime().millisecond; }
        /** Microseconds of the millisecond (0-999) */ get us() { return this.toDateTime().microsecond; }
        /** Nanoseconds of the microsecond (0-999) */ get ns() { return this.toDateTime().nanosecond; }
        /** Fractional seconds (e.g., 0.123456789) */ get ff() { return +(`0.${pad(this.ms, 3)}${pad(this.us, 3)}${pad(this.ns, 3)}`); }
        /** IANA Time Zone ID (e.g., 'Australia/Sydney') */ get tz() { return this.#temporalIds()[0]; }
        /** Temporal Calendar ID (e.g., 'iso8601' | 'gregory') */ get cal() { return this.#temporalIds()[1]; }
        /** Resolved BCP 47 locale (e.g., 'en-US') */ get locale() { return __classPrivateFieldGet(Tempo, _classThis, "f", _Tempo_locale).call(Tempo, this.#local.config.locale ?? this[$Internal]().config.locale); }
        /** Resolved hemisphere ('north' | 'south') */ get sphere() { return (this.#local.config.sphere ?? this[$Internal]().config.sphere ?? Default.sphere); }
        /** Unix timestamp (defaults to milliseconds) */ get ts() { return this.epoch[this.#local.config.timeStamp]; }
        /** Short month name (e.g., 'Jan') */ get mmm() { return Tempo.MONTH.keyOf(this.toDateTime().month); }
        /** Full month name (e.g., 'January') */ get mon() { return Tempo.MONTHS.keyOf(this.toDateTime().month); }
        /** Short weekday name (e.g., 'Mon') */ get www() { return Tempo.WEEKDAY.keyOf(this.toDateTime().dayOfWeek); }
        /** Full weekday name (e.g., 'Monday') */ get wkd() { return Tempo.WEEKDAYS.keyOf(this.toDateTime().dayOfWeek); }
        /** iso weekday number: Mon=1, Sun=7 */ get dow() { return this.toDateTime().dayOfWeek; }
        /** Nanoseconds since Unix epoch (BigInt) */ get nano() { return this.toDateTime().epochNanoseconds; }
        /** Standard ISO 8601 string in UTC */ get iso() { return this.toDateTime().toInstant().toString(); }
        /** `true` if the underlying date-time is valid. */ get isValid() { return this.#resolve(zdt => !this.#errored && isZonedDateTime(zdt)); }
        /** list of registered terms and their available range keys */
        get terms() {
            const res = {};
            Tempo.terms.forEach(term => {
                const source = term.ranges || term.groups || []; // check both ranges and groups
                const list = Array.isArray(source) ? source : Object.values(source).flat(Infinity);
                const ranges = [...new Set(list.map(r => r.key).filter(isString))]; // collect unique range keys
                res[term.key] = ranges;
                if (term.scope)
                    res[term.scope] = ranges; // add scope alias if defined
            });
            return res;
        }
        /** current range key for every registered term */
        get ranges() {
            const res = {};
            Tempo.terms.forEach(term => {
                const val = this.term[term.key]; // access the term-delegate (forces evaluation)
                if (isString(val)) {
                    res[term.key] = val;
                    if (term.scope)
                        res[term.scope] = val; // alias the string to the scope key
                }
            });
            return res;
        }
        /** current Tempo configuration */
        get config() {
            const global = this[$Internal]().config;
            const out = markConfig(Object.create(global));
            Object.entries(this.#local.config).forEach(([k, v]) => setProperty(out, k, v));
            Object.defineProperty(out, 'toJSON', {
                value: () => Object.fromEntries(// bare-bones: only show local overrides
                Object.entries(out)), // proxify sees own toJSON, skips allObject
                enumerable: false, configurable: true
            });
            return proxify(out);
        }
        /** Instance-specific parse rules (merged with global) */
        get parse() {
            const self = unwrap(this);
            self.#resolve();
            // Return a shadowed view so we can safely inject matches without breaking the freeze on the original state
            const out = Object.create(self.#local.parse);
            // Explicitly surface key debug properties as "own" properties for better visibility in consoles/REPLs
            const keys = ['anchor', 'isAnchored', 'mode', 'lazy', 'pivot'];
            for (const key of keys) {
                if (self.#local.parse[key] !== undefined)
                    Object.defineProperty(out, key, { value: self.#local.parse[key], enumerable: true, configurable: true });
            }
            // Always surface the result (prioritizing current operation matches)
            Object.defineProperty(out, 'result', {
                value: self.#matches ?? self.#local.parse.result ?? [],
                enumerable: true,
                configurable: true
            });
            return out;
        }
        /** Keyed results for all resolved terms */ get term() { return this.#term ??= this.#setDelegator('term'); }
        /** Formatted results for all pre-defined format codes */ get fmt() { return this.#fmt ??= this.#setDelegator('fmt'); }
        /** units since epoch for this date-time instance */ get epoch() { return __classPrivateFieldGet(Tempo, _classThis, "m", _Tempo_getEpoch).call(Tempo, this.toDateTime()); }
        /**
         * @Immutable class decorators wrap the class but leave internal lexical bindings pointing to the original, undecorated class.
         * To ensure new instances returned by instance methods are properly frozen,
         * we must instantiate internally from the decorated wrapper (which is bound to `this.constructor`)
         * rather than using `new Tempo(..)`.
         */
        /** @internal */ get #Tempo() { return this.constructor; }
        /** @internal */ get #now() { return this.#instant ??= instant(); }
        /** apply a custom format. */ format(fmt, options) { return this.#resolve(() => interpret(this, 'FormatModule', () => `{${String(fmt)}}`, false, fmt, options)); }
        until(arg0, arg1) { return this.#resolve(() => interpret(this, 'DurationModule', undefined, false, 'until', arg0, arg1) ?? this); }
        since(arg0, arg1) { return this.#resolve(() => interpret(this, 'DurationModule', undefined, false, 'since', arg0, arg1) ?? this); }
        /** returns a new `Tempo` with specific duration added. */ add(tempo, options) { return this.#resolve(() => interpret(this, 'MutateModule', 'add', false, tempo, options) ?? this); }
        /** returns a new `Tempo` with specific duration subtracted. */ subtract(tempo, options) { return this.#resolve(() => interpret(this, 'MutateModule', 'subtract', false, tempo, options) ?? this); }
        /** @hidden */ sub(tempo, options) { return this.subtract(tempo, options); }
        /** returns a new `Tempo` with specific offsets. */ set(tempo, options) { return this.#resolve(() => interpret(this, 'MutateModule', 'set', false, tempo, options) ?? this); }
        /** returns a clone of the current `Tempo` instance. */ clone() { return new this.#Tempo(this, this.config); }
        /** returns the underlying Temporal.ZonedDateTime */ toDateTime() { return this.#resolve(); }
        /** returns a Temporal.PlainDate representation */ toPlainDate() { return this.toDateTime().toPlainDate(); }
        /** returns a Temporal.PlainTime representation */ toPlainTime() { return this.toDateTime().toPlainTime(); }
        /** returns a Temporal.PlainDateTime representation */ toPlainDateTime() { return this.toDateTime().toPlainDateTime(); }
        /** returns the underlying Temporal.Instant */ toInstant() { return this.toDateTime().toInstant(); }
        /** the current system time localized to this instance. */ toNow() {
            const [tz, cal] = getTemporalIds(this.#local.config.timeZone, this.#local.config.calendar);
            return instant().toZonedDateTimeISO(tz).withCalendar(cal);
        }
        /** the date-time as a standard `Date` object. */ toDate() { return new Date(this.toDateTime().round({ smallestUnit: enums.ELEMENT.ms }).epochMilliseconds); }
        /** Custom JSON serialization for `JSON.stringify`. */ toJSON() { return { ...this.#local.config, value: this.toString() }; }
        /** iso8601 string representation of the date-time. */
        toString() {
            return (this.isValid && !this.#errored)
                ? this.toPlainDateTime().toString({ calendarName: 'never' })
                : String(this.#tempo ?? '');
        }
        /** setup local 'config' and 'parse' rules (prototype-linked to global) */
        #setLocal(options = {}) {
            const classState = this.constructor[$Internal]();
            this.#local = Object.create(classState);
            const self = unwrap(this);
            this.#local.config = markConfig(Object.create(classState.config));
            if (classState.config.registry)
                this.#local.config.registry = Object.create(classState.config.registry);
            Object.assign(this.#local.config, { scope: 'local' });
            this.#local.parse = markConfig(Object.create(classState.parse));
            this.#local.parse.event = { ...classState.parse.event };
            this.#local.parse.period = { ...classState.parse.period };
            this.#local.parse.snippet = { ...classState.parse.snippet };
            this.#local.userProvidedKeys = new Set(classState.userProvidedKeys);
            this.#local.parse.planner = {
                ...(classState.parse.planner.layoutOrder ? { layoutOrder: [...asArray(classState.parse.planner.layoutOrder)] } : {}),
                ...(isDefined(classState.parse.planner.preFilter) ? { preFilter: Boolean(classState.parse.planner.preFilter) } : {}),
            };
            this.#local.parse.monthDay = {
                ...classState.parse.monthDay,
                locales: [...asArray(classState.parse.monthDay.locales)],
                layouts: [...asArray(classState.parse.monthDay.layouts)],
                timezones: { ...classState.parse.monthDay.timezones },
                ...(classState.parse.monthDay.resolvedLocales ? {
                    resolvedLocales: classState.parse.monthDay.resolvedLocales.map((l) => ({ ...l, timeZones: [...l.timeZones] }))
                } : {})
            };
            setProperty(this.#local.parse, 'result', [...(options.result ?? [])]);
            Object.defineProperty(this.#local, 'tempoInstance', {
                value: self,
                writable: false,
                configurable: true,
                enumerable: false
            });
            this.constructor[$setConfig](this.#local, options); // set #local config
        }
        /** parse DateTime input */
        #parse(tempo, dateTime, term) {
            if (!ensureModule(this, 'ParseModule', true))
                return this.#fallbackParse(tempo, dateTime, term);
            const res = interpret(this, 'ParseModule', 'parse', false, tempo, dateTime, term);
            if (isUndefined(res)) {
                const msg = `ParseModule error. Could not parse ${String(tempo)}`;
                logError(msg, this.#local.config);
                return undefined;
            }
            if (isObject(res) && 'value' in res) {
                return res.value ?? undefined;
            }
            return res;
        }
        #fallbackParse(tempo, dateTime, term) {
            if (isZonedDateTime(tempo))
                return tempo;
            if (isTempo(tempo))
                return tempo.toDateTime();
            if (isString(tempo)) {
                try {
                    const tz = this.#local.config.timeZone ?? 'UTC';
                    if (tempo.includes('['))
                        return Temporal.ZonedDateTime.from(tempo);
                    return Temporal.PlainDateTime.from(tempo).toZonedDateTime(tz);
                }
                catch { /* ignore and throw below */ }
            }
            if (isUndefined(tempo) || isEmpty(tempo))
                return dateTime ?? instant().toZonedDateTimeISO(this.#local.config.timeZone);
            const msg = 'Tempo ParseModule not loaded. Did you forget to Tempo.extend(ParseModule)?';
            logError(msg, this.#local.config);
            return undefined;
        }
        /** resolve constructor / method arguments */
        #swap(tempo, options = {}) {
            if (isTempo(tempo))
                // preserve parse result history when creating new instance from an existing one
                return [tempo, Object.assign({ result: [...tempo.parse.result] }, options)];
            let mappedTempo = tempo;
            if (isObject(tempo) && isDurationLike(tempo)) {
                const record = { ...tempo };
                let hasMapped = false;
                for (const [k, v] of Object.entries(record)) {
                    const resolved = enums.ELEMENT.has(k) ? `${enums.ELEMENT[k]}s` : undefined;
                    if (resolved) {
                        record[resolved] = v;
                        delete record[k];
                        hasMapped = true;
                    }
                }
                if (hasMapped)
                    mappedTempo = record;
            }
            return this.#isOptions(mappedTempo)
                ? [mappedTempo.value, Object.assign({}, mappedTempo)]
                : [mappedTempo, options];
        }
        /** check if we've been given a Tempo Options object */
        #isOptions(arg) {
            if (!isObject(arg) || arg.constructor !== Object)
                return false;
            const keys = ownKeys(arg); // if it contains any 'mutation' keys, then it's not (just) an options object
            if (keys.some(key => enums.MUTATION.has(key)))
                return false;
            // 2. If it contains any recognized Date/Time value keys (e.g. year, month, day, hours, minutes), it's likely an input value
            if (keys.some(key => (enums.ZONED_DATE_TIME.has(key) && !enums.CONFIG.has(key)) || enums.DURATIONS.has(key)))
                return false;
            // 3. If it contains any recognized Config or Parse keys, it's definitely an options object
            if (keys.some(key => enums.CONFIG.has(key) || enums.PARSE.has(key)))
                return true;
            // 4. Otherwise, it is a plain object (possibly empty), so we treat it as an options object
            return true;
        }
        #isBlocked(key) {
            const rt = getRuntime();
            const blocked = [LICENSE.Revoked, LICENSE.Expired, LICENSE.Invalid];
            if (blocked.includes(rt.license.status) && hasOwn(rt.license.scopes, key)) {
                const msg = `License for premium term '${key}' is ${rt.license.status}. Access denied.`;
                logWarn(msg, this.#local.config);
                return true;
            }
            return false;
        }
        static {
            __runInitializers(_classThis, _classExtraInitializers);
        }
    };
    return Tempo = _classThis;
})();
export { Tempo };
